import {
  useQuery,
  useQueryClient,
  useSuspenseQuery,
} from '@tanstack/react-query';
import { Image as ExpoImage } from 'expo-image';
import * as Linking from 'expo-linking';
import { useEffect, useRef, useState } from 'react';
import { withUnistyles } from 'react-native-unistyles';

import { Button, Heading, Text, VStack, theme } from '@squire/ui';

import {
  CASH_APP_PAYMENT_INTENT_QUERY_KEY,
  getCashAppPaymentIntentQuery,
} from '@/api/booking/getCashAppPaymentIntentQuery';
import { getCashAppStatusQuery } from '@/api/booking/getCashAppStatusQuery';
import { useToast } from '@/components/_shared/Toaster';
import { routes } from '@/constants/routes';
import { useStripe } from '@/hooks/payments/useStripe';
import { useScreenTranslations } from '@/hooks/useScreenTranslations';
import { saveCashAppGiftCardPaymentToSession } from '@/utils/cashAppGiftCardSessionStorage';
import { saveCashAppPaymentToSession } from '@/utils/cashAppSessionStorage';
import { isMobile, isWeb } from '@/utils/getPlatform';
import { Logger } from '@/utils/logging';
import { getPaymentReturnUrl } from '@/utils/navigationUtils';
import { PaymentMethodType } from '@/utils/paymentMethods';

import { styles } from './styles';
import type { CashAppCodeProps } from './types';

const Image = withUnistyles(ExpoImage);

const QR_CODE_EXPIRY_SECONDS = 5 * 60;

const CashAppQrCode = ({
  onPaymentSuccess,
  onPaymentError,
  customer,
  cartParams,
}: CashAppCodeProps) => {
  const { t } = useScreenTranslations('CashAppBottomSheet');

  const [timeRemaining, setTimeRemaining] = useState<number | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const callbackCalledRef = useRef<boolean>(false);
  const onPaymentSuccessRef = useRef(onPaymentSuccess);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const stripe = useStripe();

  const currentAppUrl = getPaymentReturnUrl(
    routes.cashAppReturn({
      shopRoute: cartParams.shopRoute,
      saleOrderId: cartParams.cartId,
    }),
  );

  const { data, refetch, isRefetching } = useSuspenseQuery(
    getCashAppPaymentIntentQuery(stripe, currentAppUrl),
  );

  const {
    isLoading: isStatusLoading,
    data: statusData,
    isSuccess: isStatusSuccess,
    isError: isStatusError,
    error: statusError,
  } = useQuery(
    getCashAppStatusQuery(stripe, data.client_secret, !!data.client_secret),
  );

  const { image_url_png: qrCodeImageUrl } =
    data?.next_action?.cashapp_handle_redirect_or_display_qr_code?.qr_code ??
    {};

  const { mobile_auth_url: mobileAuthUrl } =
    data?.next_action?.cashapp_handle_redirect_or_display_qr_code ?? {};

  const formatTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;

    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const handleOpenCashAppDeepLink = async () => {
    try {
      const canOpen = await Linking.canOpenURL(mobileAuthUrl);
      if (canOpen) {
        await Linking.openURL(mobileAuthUrl ?? '');
      } else {
        Logger.warn('[CashAppQrCode] Cash App URL cannot be opened', {
          mobileAuthUrl,
        });
        toast({
          message: t(
            'error.openAppError',
            'Could not open Cash App. Please scan the QR code instead.',
          ),
          variant: 'error',
        });
      }
    } catch (err) {
      Logger.error('[CashAppQrCode] Failed to open Cash App deep link', {
        error: err,
      });
      toast({
        message: t(
          'error.openAppError',
          'Could not open Cash App. Please scan the QR code instead.',
        ),
        variant: 'error',
      });
    }
  };

  useEffect(() => {
    if (!qrCodeImageUrl) return;

    setTimeRemaining(QR_CODE_EXPIRY_SECONDS);

    intervalRef.current = setInterval(() => {
      setTimeRemaining((prev) => (prev !== null && prev > 0 ? prev - 1 : 0));
    }, 1000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [qrCodeImageUrl]);

  useEffect(() => {
    onPaymentSuccessRef.current = onPaymentSuccess;
  }, [onPaymentSuccess]);

  useEffect(() => {
    if (
      isStatusSuccess &&
      !callbackCalledRef.current &&
      statusData?.status?.toLowerCase() === 'succeeded'
    ) {
      callbackCalledRef.current = true;
      onPaymentSuccessRef.current?.({
        id: 'cashapp',
        type: PaymentMethodType.cardless,
        token: statusData.payment_method,
      });
    }
  }, [isStatusSuccess, statusData]);

  useEffect(() => {
    callbackCalledRef.current = false;
  }, [data.client_secret]);

  useEffect(() => {
    if (!data?.client_secret) return;
    if (cartParams.giftCardData) {
      saveCashAppGiftCardPaymentToSession({
        cartId: cartParams.cartId,
        shopId: cartParams.giftCardData.shopId,
        amountCents: cartParams.giftCardData.amountCents,
        currency: cartParams.giftCardData.currency,
        clientSecret: data.client_secret,
        customer,
      });
    } else {
      saveCashAppPaymentToSession({
        clientSecret: data.client_secret,
        customer,
        ...cartParams,
      });
    }
  }, [data?.client_secret, customer, cartParams]);

  useEffect(() => {
    if (!isStatusLoading && isStatusError) {
      toast({
        message: t(
          'error.paymentError',
          'There was an error processing your payment. Please try again.',
        ),
        variant: 'error',
        duration: 5 * 1000,
      });

      onPaymentError?.(statusError.message);
    }
  }, [isStatusError, t, toast, onPaymentError, statusError, isStatusLoading]);

  // SQR-15545: the intent is single-use and cached for 5 minutes. Evict it on
  // unmount so the next open mints a fresh one — never while mounted, or the
  // live observer rebuilds the query and POSTs a stray confirmed intent.
  useEffect(
    () => () => {
      queryClient.removeQueries({
        queryKey: CASH_APP_PAYMENT_INTENT_QUERY_KEY,
      });
    },
    [queryClient],
  );

  return (
    <VStack centerVertical space={theme.spacing[5]}>
      {!!timeRemaining && timeRemaining >= 0 ? (
        <>
          <Image
            contentFit="cover"
            source={qrCodeImageUrl}
            style={styles.qrCodeImage}
            testID="qr-code-image"
          />

          <Text variant="lStrong" color="textPrimary" align="center">
            {t('timeRemaining')}: {formatTime(timeRemaining)}
          </Text>
        </>
      ) : (
        <VStack centerVertical centerHorizontal space={theme.spacing[5]}>
          <Heading variant="s">{t('qrCodeExpired')}</Heading>

          <Text variant="m" color="textPrimary" align="center">
            {t('qrCodeExpiredDescription')}
          </Text>

          <Button
            loading={isRefetching}
            style={styles.retryButton}
            onPress={() => refetch()}
          >
            {t('retry')}
          </Button>
        </VStack>
      )}

      {(!isWeb() || isMobile()) && !!timeRemaining && timeRemaining > 0 && (
        <Button variant="textLink" onPress={handleOpenCashAppDeepLink}>
          {t('openInCashApp')}
        </Button>
      )}

      <VStack space={theme.spacing[8]} paddingHorizontal={theme.spacing[5]}>
        <Text variant="l" color="textSecondary" align="center">
          {t('instructions')}
        </Text>

        <Text variant="m" align="center" color="textTertiary">
          {t('troubleshooting')}
        </Text>
      </VStack>
    </VStack>
  );
};

CashAppQrCode.displayName = 'CashAppQrCode';
export { CashAppQrCode };
